import React, { useState, useEffect, useCallback } from 'react';
import { activityAPI } from '../lib/api';
import { Clock, MessageSquare, FileText, CheckSquare, DollarSign, FolderOpen } from 'lucide-react';

const ACTION_ICONS = {
  created_project: FolderOpen,
  created_task: CheckSquare,
  updated_task: CheckSquare,
  added_comment: MessageSquare,
  created_invoice: DollarSign,
  added_budget_item: DollarSign,
  uploaded_file: FileText,
};

const ACTION_COLORS = {
  created_project: 'text-emerald-600 bg-emerald-100',
  created_task: 'text-blue-600 bg-blue-100',
  updated_task: 'text-amber-600 bg-amber-100',
  added_comment: 'text-purple-600 bg-purple-100',
  created_invoice: 'text-green-600 bg-green-100',
  added_budget_item: 'text-orange-600 bg-orange-100',
  uploaded_file: 'text-cyan-600 bg-cyan-100',
};

export default function ActivityLog({ projectId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await activityAPI.getProjectActivity(projectId);
      setLogs(res.data);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 172800000) return 'yesterday';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1,2,3].map(i => <div key={i} className="h-12 bg-muted rounded" />)}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground" data-testid="activity-log-empty">
        <Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No activity recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="activity-log">
      {logs.map((log, idx) => {
        const IconComponent = ACTION_ICONS[log.action] || Clock;
        const colorClass = ACTION_COLORS[log.action] || 'text-gray-600 bg-gray-100';
        
        return (
          <div key={log.log_id} className="flex gap-3 py-2.5 group" data-testid={`activity-${log.log_id}`}>
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                <IconComponent className="h-3.5 w-3.5" />
              </div>
              {idx < logs.length - 1 && <div className="w-px h-full bg-border mt-1" />}
            </div>
            <div className="flex-1 min-w-0 pb-2">
              <p className="text-sm">
                <span className="font-medium">{log.user_name}</span>
                <span className="text-muted-foreground ml-1">{log.details}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{formatTime(log.created_at)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
